import store from '@/store';
import decode from 'jwt-decode';
import multiguard from 'vue-router-multiguard';
import { account } from '../../routes.account';
import { hasAccess, isActive, PLAN_TOOLS, ROLES_ACCESS } from '../utils';

const ifAuthenticatedAndSupplier = async (to, from, next) => {
  if (store.getters.isAuthenticated) {
    await store.dispatch(
      'get_user',
      decode(sessionStorage.getItem('access_token_4') || localStorage.getItem('access_token'))._id
    );
    if (store.getters.needToSetupUser) {
      next('/signup');
    } else if (store.getters.loggedInUser.supplierId) {
      next();
    } else {
      next('/');
    }
    return;
  }
  next({ path: '/signin', query: { redirect: to.fullPath } });
};

const supplierRoutes = [
  {
    path: '/supplier',
    name: 'supplier/index',
    meta: {
      id: ROLES_ACCESS.SUPPLIERS
    },
    component: () => import('@/portals/Supplier/Index'),
    beforeEnter: multiguard([ifAuthenticatedAndSupplier, isActive, hasAccess]),
    children: [
      ...account('supplier'),
      {
        path: 'dashboard',
        name: 'supplier/dashboard',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS
        },
        component: () => import('@/portals/Supplier/Dashboard')
      },
      {
        path: 'search',
        name: 'supplier/search',
        meta: { available: true },
        component: () => import('@/components/search/global/SearchResultsPage'),
        props: { searchPortal: 'supplier' }
      },
      {
        path: 'forum',
        name: 'supplier/forum',
        meta: { available: true, requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        component: () => import('@/portals/Application/Forum/ForumHome')
      },
      {
        path: 'forum/new',
        name: 'supplier/forum-new',
        meta: { available: true, requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        component: () => import('@/portals/Application/Forum/PostComposer')
      },
      {
        path: 'forum/messages',
        name: 'supplier/forum-messages',
        meta: { available: true, requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        component: () => import('@/portals/Application/Forum/ForumInbox')
      },
      {
        path: 'forum/messages/:id',
        name: 'supplier/forum-conversation',
        meta: { available: true, requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        component: () => import('@/portals/Application/Forum/ForumConversation')
      },
      {
        path: 'forum/:id',
        name: 'supplier/forum-post',
        meta: { available: true, requiredTools: [PLAN_TOOLS.FORUM[0].value] },
        component: () => import('@/portals/Application/Forum/PostDetail')
      },
      {
        path: 'products',
        name: 'supplier/products',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-products-section']
        },
        component: () => import('@/portals/Supplier/Products/Index')
      },
      {
        path: 'products/create',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-products-section']
        },
        component: () => import('@/portals/Administration/Products/Form')
      },
      {
        path: 'products/:slug/edit',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-products-section']
        },
        component: () => import('@/portals/Administration/Products/Form')
      },
      {
        path: 'ads',
        name: 'supplier/ads',
        meta: {
          id: ROLES_ACCESS.SUPPLIER_ADS,
          requiredTools: ['show-supplier-ads-section']
        },
        component: () => import('@/portals/Supplier/Products/AdsProducts')
      },
      {
        path: 'banner-ads',
        redirect: { path: '/supplier/ads', query: { tab: 'banner-campaigns' } }
      },
      {
        path: 'stores',
        name: 'supplier/stores',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-stores-section']
        },
        component: () => import('@/components/Stores/List')
      },
      {
        path: 'stores/create',
        name: 'supplier/stores/create',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-stores-section']
        },
        component: () => import('@/components/Stores/Form')
      },
      {
        path: 'stores/:storeId/products/create',
        name: 'supplier/stores/:storeId/products/create',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-stores-section']
        },
        component: () => import('@/components/Stores/Editor/Products/form')
      },
      {
        path: 'stores/:storeId',
        name: 'supplier/stores/:storeId',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS,
          requiredTools: ['show-supplier-stores-section']
        },
        component: () => import('@/components/Stores/Editor/Index')
      },
      {
        path: 'users',
        name: 'supplier/users',
        meta: {
          id: ROLES_ACCESS.ADMIN_SUPPLIER
        },
        component: () => import('@/components/UsersList')
      },
      {
        path: 'users/create',
        name: 'supplier/users/create',
        meta: {
          id: ROLES_ACCESS.ADMIN_SUPPLIER
        },
        component: () => import('@/components/UserForm')
      },
      {
        path: 'users/:userId/edit',
        name: 'supplier/users/:userId/edit',
        meta: {
          id: ROLES_ACCESS.ADMIN_SUPPLIER
        },
        component: () => import('@/components/UserForm')
      },
      {
        path: 'users/:userId/changepassword',
        name: 'supplier/users/:userId/changepassword',
        meta: {
          id: ROLES_ACCESS.ADMIN_SUPPLIER
        },
        component: () => import('@/components/ChangePassword')
      },
      {
        path: ':supplierId/info',
        name: 'supplier/info',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS
        },
        component: () => import('@/portals/Administration/Suppliers/View')
      },
      {
        path: ':supplierId/edit',
        name: 'supplier/edit',
        meta: {
          id: ROLES_ACCESS.SUPPLIERS
        },
        component: () => import('@/portals/Administration/Suppliers/SupplierForm')
      },
      {
        name: 'production/calendar',
        path: 'production/calendar',
        component: () => import('@/portals/Supplier/PurchaseOrders/Calendar'),
        meta: {
          id: ROLES_ACCESS.SUPPLIER_PRODUCTION,
          requiredTools: ['show-supplier-calendar-section']
        }
      },
      {
        name: 'production/machines',
        path: 'production/machines',
        component: () => import('@/portals/Supplier/Machines'),
        meta: {
          id: ROLES_ACCESS.SUPPLIER_PRODUCTION,
          requiredTools: ['show-supplier-machines-section']
        }
      },
      {
        name: 'supplier/reports',
        path: 'reports',
        component: () => import('@/portals/Supplier/Reports'),
        meta: {
          id: ROLES_ACCESS.SUPPLIER_PRODUCTION,
          requiredTools: ['show-supplier-reporting-section']
        },
        children: [
          {
            name: 'supplier/reports/default',
            path: '/',
            component: () => import('@/components/Suppliers/Reports/StoreImpressions')
          },
          {
            name: 'supplier/reports/store',
            path: 'store-impressions',
            component: () => import('@/components/Suppliers/Reports/StoreImpressions')
          },
          {
            name: 'supplier/reports/category',
            path: 'category-impressions',
            component: () => import('@/components/Suppliers/Reports/CategoryImpressions')
          },
          {
            name: 'supplier/reports/product',
            path: 'product-impressions',
            component: () => import('@/components/Suppliers/Reports/ProductImpressions')
          },
          {
            name: 'supplier/reports/app',
            path: 'app-impressions',
            component: () => import('@/components/Suppliers/Reports/AppImpressions')
          },
          {
            name: 'supplier/reports/orders-analytics',
            path: 'orders-analytics',
            component: () => import('@/components/Suppliers/Reports/OrdersAnalytics')
          }
        ]
      },
      {
        name: 'production/board',
        path: 'production/board',
        component: () => import('@/portals/Supplier/Board'),
        meta: {
          id: ROLES_ACCESS.SUPPLIER_PRODUCTION,
          requiredTools: ['show-supplier-board-section']
        }
      },
      {
        name: 'production/orders',
        path: 'production/orders',
        component: () => import('@/portals/Supplier/PurchaseOrders/List'),
        meta: {
          id: ROLES_ACCESS.SUPPLIER_PRODUCTION,
          requiredTools: ['show-supplier-orders-section']
        }
      },
      {
        name: 'production/orders/:orderId',
        path: 'production/orders/:orderId',
        component: () => import('@/portals/Supplier/PurchaseOrders/View'),
        meta: {
          id: ROLES_ACCESS.SUPPLIER_PRODUCTION,
          requiredTools: ['show-supplier-orders-section']
        }
      }
    ].map((el) => ({ ...el, beforeEnter: hasAccess }))
  }
];
export { supplierRoutes as default };
